import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { COMPANY_INFO } from '../lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const NAV_ITEMS = [
  { label: 'Accueil', href: '/' },
  { label: 'Notre vision', href: '/notre-vision' },
  { 
    label: 'Services', 
    href: '/services',
    children: [
      { label: 'Entretien ménager', href: '/services/entretien-menager' },
      { label: 'Entretien du linge', href: '/services/entretien-linge' },
      { label: 'Nettoyage des vitres', href: '/services/nettoyage-vitres' },
      { label: 'Courses et sorties', href: '/services/courses-sorties' },
      { label: 'Jardinage', href: '/services/espaces-verts' },
      { label: 'Petit bricolage', href: '/services/petit-bricolage' },
      { label: 'Aide aux repas', href: '/services/aide-repas' },
      { label: 'Assistance admin.', href: '/services/assistance-administrative' },
      { label: 'Global Service Domicile', href: '/services/global-service-domicile' },
      { label: 'Global Service Jardin', href: '/services/global-service-jardin' },
      { label: 'Chouette Apprentissage offert', href: '/chouette-apprentissage' },
    ]
  },
  { label: 'Crédit d\'impôt', href: '/credit-impot' },
  { label: 'Témoignages', href: '/temoignages' },
  { label: 'Ptitconseils', href: '/ptitconseils' },
  { label: 'Blog', href: '/blog' },
  { 
    label: 'Nos agences', 
    href: '/agence/artois.ptitservices',
    children: [
      { label: 'Agence Artois', href: '/agence/artois.ptitservices' },
      { label: 'Villes desservies', href: '/services-a-domicile' },
    ]
  },
  { label: 'Devenir Easer', href: '/devenir-easer', highlight: true },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [globalRating, setGlobalRating] = useState({ average: 0, total: 0 });
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reviews/global-stats`)
      .then(res => setGlobalRating(res.data))
      .catch(() => {});
  }, []);

  return (
    <>
      {/* Top bar */}
      <div className="bg-[hsl(var(--primary))] text-primary-foreground py-2 px-4 text-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="flex items-center gap-2 hover:opacity-90" data-testid="header-phone-link">
            <Phone className="h-3.5 w-3.5" />
            <span className="font-medium">{COMPANY_INFO.phone}</span>
          </a>
          <span className="hidden sm:flex items-center gap-2 text-xs opacity-90">
            {globalRating.total > 0 && (
              <Link to="/temoignages" className="flex items-center gap-1 hover:opacity-80">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                <span className="font-semibold">{globalRating.average}/5</span>
                <span className="opacity-70">({globalRating.total} avis)</span>
              </Link>
            )}
            {globalRating.total === 0 && <span>Crédit d'impôt 50% &bull; Avance immédiate</span>}
          </span>
          <Link to="/devis">
            <Button size="sm" variant="secondary" className="h-7 text-xs font-medium" data-testid="header-quote-button">
              Demander un devis
            </Button>
          </Link>
        </div>
      </div>

      {/* Main header */}
      <header className={`sticky top-0 z-50 border-b border-border transition-shadow duration-200 ${
        scrolled ? 'shadow-md bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70' : 'bg-background'
      }`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png" alt="Ptitservices.com - Facilitateur de vies" className="h-10 w-auto rounded-lg" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              item.children ? (
                <div key={item.label} className="relative" onMouseEnter={() => setOpenDropdown(item.label)} onMouseLeave={() => setOpenDropdown(null)}>
                  <Link to={item.href} className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md hover:bg-secondary transition-colors ${
                    location.pathname.startsWith('/services') ? 'text-[hsl(var(--primary))]' : 'text-foreground'
                  }`}>
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Link>
                  {openDropdown === item.label && (
                    <div className="absolute top-full left-0 w-64 bg-card border border-border rounded-lg shadow-lg py-2 mt-0">
                      {item.children.map((child) => (
                        <Link key={child.href} to={child.href} className="block px-4 py-2 text-sm hover:bg-secondary transition-colors">
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.label} to={item.href} translate="no" data-testid={item.highlight ? 'nav-devenir-easer' : undefined} className={`notranslate px-3 py-2 text-sm font-medium rounded-md hover:bg-secondary transition-colors ${
                  location.pathname === item.href ? 'text-[hsl(var(--primary))]' : item.highlight ? 'text-[hsl(var(--accent))]' : 'text-foreground'
                }`}>
                  {item.label}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/rendez-vous">
              <Button variant="outline" size="sm" data-testid="header-appointment-button">
                Prendre rendez-vous
              </Button>
            </Link>
            <Link to="/devis">
              <Button size="sm" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
                Devis gratuit
              </Button>
            </Link>
          </div>

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="header-mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col h-full overflow-hidden" data-testid="mobile-menu-panel">
              <div className="flex items-center gap-2 px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
                <img src="https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png" alt="Ptitservices.com" className="h-9 w-auto rounded-lg" />
              </div>
              {/* Scrollable navigation */}
              <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3" data-testid="mobile-menu-scroll">
                <nav className="space-y-0.5">
                  {NAV_ITEMS.map((item) => (
                    <div key={item.label}>
                      <Link
                        to={item.href}
                        translate="no"
                        onClick={() => setMobileOpen(false)}
                        data-testid={item.highlight ? 'mobile-nav-devenir-easer' : `mobile-nav-${item.href.replace(/\W+/g, '-').replace(/^-|-$/g, '') || 'accueil'}`}
                        className={`notranslate flex items-center justify-between px-3 py-2.5 text-[15px] font-medium rounded-md hover:bg-secondary active:bg-secondary ${
                          item.highlight ? 'text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5 border border-[hsl(var(--accent))]/20 mt-1' : location.pathname === item.href ? 'text-[hsl(var(--primary))]' : ''
                        }`}
                      >
                        {item.label}
                        {item.highlight && <ChevronDown className="h-4 w-4 -rotate-90" />}
                      </Link>
                      {item.children && (
                        <div className="ml-3 mb-1 grid grid-cols-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => setMobileOpen(false)}
                              className="block px-3 py-1.5 text-[13px] text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md truncate"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
              {/* Sticky actions */}
              <div className="flex-shrink-0 border-t border-border px-4 py-3 space-y-2 bg-card" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/rendez-vous" onClick={() => setMobileOpen(false)} className="block">
                    <Button variant="outline" size="sm" className="w-full">Rendez-vous</Button>
                  </Link>
                  <Link to="/devis" onClick={() => setMobileOpen(false)} className="block">
                    <Button size="sm" className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">Devis gratuit</Button>
                  </Link>
                </div>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="flex items-center justify-center gap-2 py-1.5 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  {COMPANY_INFO.phone}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </>
  );
}
